import React, { useEffect, useRef } from 'react';
import { SEAT_CONTAINER_HEIGHT, SEAT_CONTAINER_WIDTH } from 'constants/gameConstants';

type FlameCanvasProps = {
  intensity?: number; // 0.5 = mild, 1 = normal, 1.5 = intense
  color?: 'fire' | 'green' | 'purple';
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
};

// Exported for the streak celebration (EN-11163), which runs the same fire
// palette upward across the felt.
export const FIRE_COLORS = [
  [255, 255, 255], // white core
  [255, 255, 100], // bright yellow
  [255, 200, 50], // yellow
  [255, 140, 20], // orange
  [255, 80, 10], // deep orange
  [200, 30, 0], // red
  [100, 10, 0] // dark red
];

const GREEN_COLORS = [
  [255, 255, 255],
  [150, 255, 150],
  [80, 255, 100],
  [30, 200, 60],
  [10, 150, 40],
  [5, 100, 20],
  [0, 50, 10]
];

const PURPLE_COLORS = [
  [255, 255, 255],
  [200, 170, 255],
  [159, 122, 234],
  [128, 90, 213],
  [107, 70, 193],
  [85, 50, 160],
  [55, 30, 110]
];

const FlameCanvas = ({ intensity = 1, color = 'fire' }: FlameCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animRef = useRef<number>(0);

  const canvasPadding = 88;
  const size = Math.max(SEAT_CONTAINER_WIDTH, SEAT_CONTAINER_HEIGHT) + canvasPadding * 2;
  const centerX = size / 2;
  const centerY = size / 2;
  const avatarRadius = SEAT_CONTAINER_WIDTH / 2;
  const colors =
    color === 'green' ? GREEN_COLORS : color === 'purple' ? PURPLE_COLORS : FIRE_COLORS;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const spawnRate = Math.floor(3 * intensity);

    const getColor = (lifeRatio: number): string => {
      const idx = lifeRatio * (colors.length - 1);
      const i = Math.floor(idx);
      const t = idx - i;
      const c1 = colors[Math.min(i, colors.length - 1)];
      const c2 = colors[Math.min(i + 1, colors.length - 1)];
      const r = c1[0] + (c2[0] - c1[0]) * t;
      const g = c1[1] + (c2[1] - c1[1]) * t;
      const b = c1[2] + (c2[2] - c1[2]) * t;
      const alpha = lifeRatio < 0.3 ? lifeRatio / 0.3 : 1 - (lifeRatio - 0.3) / 0.7;
      return `rgba(${r | 0},${g | 0},${b | 0},${alpha.toFixed(2)})`;
    };

    const spawn = () => {
      for (let i = 0; i < spawnRate; i++) {
        // Spawn around the avatar edge
        const angle = Math.random() * Math.PI * 2;
        const r = avatarRadius + (Math.random() - 0.5) * 4;
        const x = centerX + Math.cos(angle) * r;
        const y = centerY + Math.sin(angle) * r;

        // Velocity: mostly upward with slight outward push
        const outward = 0.2 + Math.random() * 0.3;
        const vx = Math.cos(angle) * outward + (Math.random() - 0.5) * 0.5;
        const vy = -(1.5 + Math.random() * 1.5) * intensity;

        particlesRef.current.push({
          x,
          y,
          vx,
          vy,
          life: 0,
          maxLife: 20 + Math.random() * 25,
          size: 3 + Math.random() * 4 * intensity
        });
      }
    };

    // Throttle to ~30fps. At 60fps with ~3 spawns/frame and dozens of
    // simultaneous canvases (multiple players' actions visible), the flame
    // dominates the main thread on iOS WKWebView. Fire is naturally low-
    // frequency so 30fps still looks fine, and we halve the per-frame cost.
    const FRAME_INTERVAL_MS = 1000 / 30;
    let lastFrameMs = 0;

    const update = (ts: number) => {
      animRef.current = requestAnimationFrame(update);
      if (ts - lastFrameMs < FRAME_INTERVAL_MS) return;
      lastFrameMs = ts;

      ctx.clearRect(0, 0, size, size);

      spawn();

      const alive: Particle[] = [];
      for (const p of particlesRef.current) {
        p.life++;
        if (p.life > p.maxLife) continue;

        // Turbulence
        p.vx += (Math.random() - 0.5) * 0.15;
        p.vy -= 0.02; // slight upward acceleration
        p.x += p.vx;
        p.y += p.vy;

        const lifeRatio = p.life / p.maxLife;
        const fadeOut = Math.max(0, 1 - lifeRatio * 2);
        const currentSize = p.size * (1 - lifeRatio * 0.7) * fadeOut;

        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(currentSize, 0.5), 0, Math.PI * 2);
        ctx.fillStyle = getColor(lifeRatio);
        ctx.fill();

        alive.push(p);
      }
      particlesRef.current = alive;
    };

    animRef.current = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(animRef.current);
      particlesRef.current = [];
    };
  }, [intensity, color]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: -(size - SEAT_CONTAINER_HEIGHT) / 2,
        left: -(size - SEAT_CONTAINER_WIDTH) / 2,
        width: size,
        height: size,
        pointerEvents: 'none',
        zIndex: 15
      }}
    />
  );
};

export default FlameCanvas;
